import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Play } from 'lucide-react';
import AmbientBackground from './AmbientBackground';
import TrustBadge from './TrustBadge';

const HeroSection = () => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  return (
    <section className="relative min-h-[80vh] flex items-center pt-20" style={{ overflow: 'visible' }}>
      {/* Background */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <div className="absolute inset-0 bg-black" />
        <AmbientBackground variant="hero" />
      </div>

      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 grid-pattern z-[1] overflow-hidden" />

      {/* Content */}
      <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12 py-8 md:py-12 w-full">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Text */}
          <div className="order-1">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] mb-4 heading">
              Get Paid at Sale Agreed.
              <br />
              <span className="text-[#59d862]">Not Completion.</span>
            </h1>

            <p className="text-lg text-white/60 leading-relaxed mb-4 max-w-lg">
              Agents have been conditioned to wait months to get paid. <span className="text-white font-medium">We just broke that rule.</span> With Gazeal Advance, agents can access their commission when an offer is accepted and secured.
            </p>

            {/* Limited rollout badge */}
            <div className="mb-6 max-w-lg">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#c4b9a6]/10 border border-[#c4b9a6]/30 rounded-full">
                <span className="w-1.5 h-1.5 bg-[#c4b9a6] rounded-full" />
                <span className="text-[#c4b9a6] text-xs font-medium">Limited rollout. By application only.</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/get-paid"
                className="btn-primary text-base px-6 py-3 flex items-center justify-center gap-2 group"
              >
                Get Paid Now
                <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/apply"
                className="btn-secondary text-base px-6 py-3 flex items-center justify-center"
              >
                Apply to Join
              </Link>
            </div>
          </div>

          {/* Video Card - Inline Player */}
          <div className="relative max-w-sm mx-auto lg:mx-0 lg:ml-auto w-full order-2">
            <div className="relative aspect-square">
              {/* Decorative Corners - hide when playing */}
              {!isVideoPlaying && (
                <>
                  <div className="absolute -top-2 -left-2 w-12 h-12 border-l-2 border-t-2 border-[#59d862]/40 rounded-tl-2xl" />
                  <div className="absolute -bottom-2 -right-2 w-12 h-12 border-r-2 border-b-2 border-[#59d862]/40 rounded-br-2xl" />
                </>
              )}
              
              {/* Video/Thumbnail Container */}
              {isVideoPlaying ? (
                /* Video Player - No overflow restrictions */
                <div className="w-full h-full rounded-2xl border border-[#59d862]/30 shadow-[0_0_40px_rgba(89,216,98,0.15)]">
                  <iframe
                    src="https://player.vimeo.com/video/1156755699?autoplay=1"
                    className="w-full h-full rounded-2xl"
                    style={{ border: 'none' }}
                    allow="autoplay; fullscreen; picture-in-picture"
                    allowFullScreen={true}
                    title="Gazeal Advance"
                  />
                </div>
              ) : (
                /* Thumbnail State */
                <div 
                  className="relative w-full h-full bg-gradient-to-br from-[#004048]/50 to-black/80 border border-white/10 rounded-2xl flex flex-col items-center justify-center p-5 cursor-pointer group hover:border-[#59d862]/30 transition-colors"
                  onClick={() => setIsVideoPlaying(true)}
                >
                  {/* Play Button */}
                  <div className="w-16 h-16 md:w-20 md:h-20 bg-[#59d862] rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform relative">
                    <Play className="w-7 h-7 md:w-8 md:h-8 text-black ml-1" fill="black" />
                    <div className="absolute inset-0 bg-[#59d862] rounded-full animate-ping opacity-20" />
                  </div>
                  
                  <div className="text-center">
                    <div className="text-white text-base md:text-lg font-semibold mb-1 heading">
                      "That word is stability."
                    </div>
                    <div className="text-white/50 text-sm">
                      Bryan Mansell, Gazeal
                    </div>
                  </div>

                  <div className="absolute bottom-3 right-3 px-3 py-1 bg-black/50 text-white/60 text-xs rounded-full backdrop-blur-sm">
                    2:30
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Trust Badge - Integrated into Hero */}
        <TrustBadge />
      </div>
    </section>
  );
};

export default HeroSection;
